<template>
  <div class="search-result">
    <!-- 搜索头部 -->
    <div class="search-header">
      <h1 class="search-title">适用于你</h1>
      <div class="search-info" v-if="!loading && !error">
        <span class="keyword-highlight">"{{ keyword }}"</span>
        找到 <strong>{{ matchedList.length }}</strong> 个结果
      </div>
      
      <!-- 重新搜索框 -->
      <div class="research-box">
        <input
          v-model="newSearchQuery"
          @keydown.enter="reSearch"
          class="research-input"
          placeholder="继续描述你的问题..."
        />
      </div>
    </div>

    <!-- 加载状态 -->
    <div v-if="loading" class="loading">
      <div class="loading-spinner"></div>
      <p>正在搜索文档...</p>
    </div>

    <!-- 错误状态 -->
    <div v-else-if="error" class="error">
      <div class="error-icon">⚠️</div>
      <p>{{ error }}</p>
      <button @click="retry" class="retry-btn">重试</button>
    </div>

    <!-- 搜索结果 -->
    <div v-else class="results-container">
      <!-- 无结果 -->
      <div v-if="matchedList.length === 0" class="no-results">
        <div class="no-results-icon">🔍</div>
        <h3>没有找到相关文档</h3>
        <p>如果无法解决你的问题，可以联系我们获得帮助</p>
      </div>

      <!-- 主要结果（第一个最相关的结果） -->
      <div v-else-if="matchedList.length > 0" class="main-result-section">
        <div class="main-result-card" @click="goTo(matchedList[0].path)">
          <div class="main-result-content">
            <div class="main-result-badge">最相关结果</div>
            <h2 class="main-result-title" v-html="highlightKeyword(matchedList[0].title)"></h2>
            <div class="main-result-path">
              <span class="breadcrumb-item" v-for="(crumb, idx) in matchedList[0].breadcrumbs" :key="idx">
                {{ crumb }}
                <span v-if="idx < matchedList[0].breadcrumbs.length - 1" class="breadcrumb-separator">›</span>
              </span>
            </div>
            <button class="main-goto-btn">查看详细文档 →</button>
          </div>
        </div>

        <!-- 展开更多结果按钮 -->
        <div v-if="matchedList.length > 1" class="expand-section">
          <button @click="showModal = true" class="expand-btn">
            查看全部 {{ matchedList.length }} 个结果
          </button>
        </div>
      </div>
    </div>

    <!-- 人工支持区域 -->
    <div v-if="!loading" class="support-section">
      <h2 class="support-title">如果无法解决你的问题？找真人一对一帮你</h2>
      <div class="support-options">
        <!-- 电话支持 -->
        <div class="support-option">
          <a class="support-call" href="tel:4008375338">
            ☎️ (400) 837 5338
          </a>
          <p class="support-description">拨打服务热线联系我们</p>
        </div>
        
        <!-- 邮件支持 -->
        <div class="support-option">
          <div
            class="support-email"
            @click="onEmailClick"
          >
            📧 support@vds.pub
          </div>
          <p class="support-description">发送邮件给我们，我们会尽快回复</p>
        </div>

      </div>
    </div>

    <!-- 弹窗：所有搜索结果 - 添加过渡动画 -->
    <Transition name="modal" appear>
      <div v-if="showModal" class="modal-overlay" @click="showModal = false">
        <div class="modal-content" @click.stop>
          <div class="modal-header">
            <h3 class="modal-title">
              全部适用于你的结果
              <span class="result-count">({{ matchedList.length }} 个结果)</span>
            </h3>
            <button @click="showModal = false" class="modal-close">✕</button>
          </div>
          
          <div class="modal-body">
            <div class="modal-results-list">
              <div
                v-for="(item, index) in paginatedModalResults"
                :key="index"
                @click="goToAndCloseModal(item.path)"
                class="modal-result-item"
              >
                <div class="modal-result-main">
                  <h4 class="modal-result-title" v-html="highlightKeyword(item.title)"></h4>
                  <div class="modal-result-path">
                    <span class="breadcrumb-item" v-for="(crumb, idx) in item.breadcrumbs" :key="idx">
                      {{ crumb }}
                      <span v-if="idx < item.breadcrumbs.length - 1" class="breadcrumb-separator">›</span>
                    </span>
                  </div>
                </div>
                <div class="modal-result-actions">
                  <button class="modal-goto-btn">查看 →</button>
                </div>
              </div>
            </div>
            
            <!-- 弹窗分页 -->
            <div v-if="modalTotalPages > 1" class="modal-pagination">
              <button 
                @click="modalCurrentPage--" 
                :disabled="modalCurrentPage === 1"
                class="modal-page-btn"
              >
                上一页
              </button>
              <span class="modal-page-info">
                {{ modalCurrentPage }} / {{ modalTotalPages }}
              </span>
              <button 
                @click="modalCurrentPage++" 
                :disabled="modalCurrentPage === modalTotalPages"
                class="modal-page-btn"
              >
                下一页
              </button>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch } from 'vue'
import axios from 'axios'
import { DOCS_API_BASE } from '../../utils/docsApi'
import { highlightDocumentTitle } from '../../utils/docsSecurity'
import { useRoute, useRouter } from 'vue-router'

const router = useRouter()
const route = useRoute()

// 从地址栏 ?q=xxx
const keyword = ref(route.query.q || '')
const newSearchQuery = ref('')

const loading = ref(false)
const error = ref(null)
const matchedList = ref([]) // 搜索结果列表

// 弹窗状态
const showModal = ref(false)
const modalCurrentPage = ref(1)
const modalPageSize = 15

onMounted(async () => {
  document.title = `适用于你: ${keyword.value} - VDS·欢乐，在此发生。`
  newSearchQuery.value = keyword.value
  await loadData()
})

// 监听路由变化，更新搜索关键词
watch(() => route.query.q, (newQ) => {
  if (newQ !== keyword.value) {
    keyword.value = newQ || ''
    newSearchQuery.value = newQ || ''
    showModal.value = false
    modalCurrentPage.value = 1
    document.title = `适用于你: ${keyword.value} - VDS·欢乐，在此发生。`
    loadData()
  }
})

async function loadData() {
  if (!keyword.value.trim()) {
    error.value = '请输入搜索关键词'
    return
  }
  
  loading.value = true
  error.value = null
  
  try {
    // 使用新的后端搜索接口
    const res = await axios.get(`${DOCS_API_BASE}/search`, { params: { q: keyword.value } })
    matchedList.value = res.data
  } catch (err) {
    console.error('搜索失败:', err)
    error.value = '搜索服务暂时不可用，请稍后重试'
  } finally {
    loading.value = false
  }
}

// 重试加载
function retry() {
  loadData()
}

// 重新搜索
function reSearch() {
  if (!newSearchQuery.value.trim()) return
  router.push({
    name: 'search',
    query: { q: newSearchQuery.value.trim() }
  })
}

function onEmailClick() {
  // 构建邮件主题和内容
  const subject = encodeURIComponent(`关于搜索问题：${keyword.value}`)
  const body = encodeURIComponent(`您好，

我在搜索"${keyword.value}"时遇到了问题，需要您的帮助。

问题描述：
[请在此处详细描述您的问题]

谢谢！`)
  
  window.location.href = `mailto:support@vds.pub?subject=${subject}&body=${body}`
}

// 弹窗分页相关计算属性
const modalTotalPages = computed(() => Math.ceil(matchedList.value.length / modalPageSize))
const paginatedModalResults = computed(() => {
  const start = (modalCurrentPage.value - 1) * modalPageSize
  const end = start + modalPageSize
  return matchedList.value.slice(start, end)
})

// 高亮关键词
function highlightKeyword(text) {
  return highlightDocumentTitle(text, keyword.value)
}

// 点击搜索结果 -> 解析 path => 跳转到 /docs/read?path=xx/yy&name=ZZZ
function goTo(mdPath) {
  if (!mdPath) return
  
  // 假设 mdPath 类似: "/111111/222222/女孩第五期活动.md"
  const segments = mdPath.replace(/^\//, '').split('/')
  if (!segments.length) return

  // 最后一个是 "xxx.md", 去掉 .md 作为 name
  const lastSeg = segments[segments.length - 1]
  const name = lastSeg.replace(/\.md$/i, '')

  // 前面的都是子目录 => path=111111/222222
  const dirSegs = segments.slice(0, -1)
  const subPath = dirSegs.join('/')

  // 跳转 => /docs/read?path=111111/222222&name=女孩第五期活动
  router.push({
    path: '/read',
    query: {
      path: subPath,
      name: name
    }
  })
}

// 点击弹窗中的结果并关闭弹窗
function goToAndCloseModal(mdPath) {
  showModal.value = false
  goTo(mdPath)
}
</script>

<style scoped>
.support-call { text-decoration: none; }
.search-result {
  color: #fff;
  padding: 20px;
  min-height: 100vh;
  max-width: 1000px;
  margin: 0 auto;
}

/* 弹窗动画过渡效果 */
.modal-enter-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.modal-leave-active {
  transition: all 0.25s cubic-bezier(0.4, 0, 0.6, 1);
}

.modal-enter-from {
  opacity: 0;
  backdrop-filter: blur(0px);
}

.modal-enter-to {
  opacity: 1;
  backdrop-filter: blur(8px);
}

.modal-leave-from {
  opacity: 1;
  backdrop-filter: blur(8px);
}

.modal-leave-to {
  opacity: 0;
  backdrop-filter: blur(0px);
}

/* 弹窗内容动画 */
.modal-enter-active .modal-content {
  transition: all 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.modal-leave-active .modal-content {
  transition: all 0.25s cubic-bezier(0.4, 0, 0.6, 1);
}

.modal-enter-from .modal-content {
  transform: scale(0.7) translateY(-50px);
  opacity: 0;
}

.modal-enter-to .modal-content {
  transform: scale(1) translateY(0);
  opacity: 1;
}

.modal-leave-from .modal-content {
  transform: scale(1) translateY(0);
  opacity: 1;
}

.modal-leave-to .modal-content {
  transform: scale(0.8) translateY(-30px);
  opacity: 0;
}

/* 搜索头部 */
.search-header {
  margin-bottom: 40px;
  text-align: center;
}

.search-title {
  font-family: 'MiSans-Bold', Arial, sans-serif;
  font-size: 36px;
  margin: 0 0 15px;
}

.search-info {
  font-size: 16px;
  margin-bottom: 30px;
  opacity: 0.8;
}

.keyword-highlight {
  font-weight: bold;
}

.research-box {
  display: flex;
  justify-content: center;
  max-width: 600px;
  margin: 0 auto;
}

.research-input {
  width: 100%;
  padding: 12px 20px;
  border-radius: 25px;
  border: 2px solid rgba(255, 255, 255, 0.2);
  background: rgba(255, 255, 255, 0.1);
  color: white;
  font-size: 16px;
  outline: none;
  transition: all 0.3s ease;
  text-align: center;
}

.research-input::placeholder {
  color: rgba(255, 255, 255, 0.6);
}

.research-input:focus {
  border-color: #006aff;
  background: rgba(255, 255, 255, 0.15);
  box-shadow: 0 0 0 3px rgba(0, 106, 255, 0.2);
}

/* 主要结果区域 */
.main-result-section {
  margin-bottom: 60px;
}

.main-result-card {
  background: linear-gradient(135deg, rgba(0, 106, 255, 0.1), rgba(255, 255, 255, 0.05));
  border: 2px solid rgba(0, 106, 255, 0.3);
  border-radius: 20px;
  padding: 32px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.main-result-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #006aff, #00d4ff);
}

.main-result-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 16px 40px rgba(0, 106, 255, 0.2);
  border-color: rgba(0, 106, 255, 0.5);
}

.main-result-badge {
  display: inline-block;
  background: linear-gradient(90deg, #006aff, #0052cc);
  color: white;
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: bold;
  margin-bottom: 16px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.main-result-title {
  font-size: 28px;
  font-weight: bold;
  margin: 0 0 16px;
  font-family: 'MiSans-Bold', Arial, sans-serif;
  line-height: 1.3;
}

.main-result-title mark {
  background: #ffeb3b;
  color: #000;
  padding: 4px 8px;
  border-radius: 6px;
  font-weight: bold;
}

.main-result-path {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  font-size: 14px;
  opacity: 0.8;
  margin-bottom: 24px;
}

.breadcrumb-item {
  background: rgba(255, 255, 255, 0.15);
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 13px;
}

.breadcrumb-separator {
  margin: 0 4px;
  opacity: 0.6;
}

.main-goto-btn {
  background: linear-gradient(135deg, #006aff, #0052cc);
  color: white;
  border: none;
  padding: 14px 28px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
}

.main-goto-btn:hover {
  background: linear-gradient(135deg, #0052cc, #003d99);
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(0, 106, 255, 0.3);
}

/* 展开更多结果按钮 */
.expand-section {
  text-align: center;
  margin-top: 30px;
}

.expand-btn {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: rgba(255, 255, 255, 0.1);
  border: 2px solid rgba(255, 255, 255, 0.2);
  color: white;
  padding: 16px 24px;
  border-radius: 12px;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
}

.expand-btn:hover {
  background: rgba(255, 255, 255, 0.15);
  border-color: rgba(255, 255, 255, 0.3);
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
}

.expand-icon {
  font-size: 20px;
}

/* 弹窗样式 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(8px);
}

.modal-content {
  background: linear-gradient(135deg, #0c0c0c, #090909);
  border-radius: 20px;
  width: 90%;
  max-width: 800px;
  max-height: 80vh;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.modal-title {
  font-size: 20px;
  font-weight: bold;
  margin: 0;
  font-family: 'MiSans-Bold', Arial, sans-serif;
}

.result-count {
  opacity: 0.7;
  font-weight: normal;
  font-size: 16px;
}

.modal-close {
  background: none;
  border: none;
  color: white;
  font-size: 24px;
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  transition: all 0.2s ease;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-close:hover {
  background: rgba(255, 255, 255, 0.1);
  transform: rotate(90deg);
}

.modal-body {
  padding: 0;
  max-height: calc(80vh - 100px);
  overflow-y: auto;
}

.modal-results-list {
  padding: 24px;
}

.modal-result-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  margin-bottom: 16px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.modal-result-item:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.2);
  transform: translateY(-2px);
}

.modal-result-main {
  flex: 1;
}

.modal-result-title {
  font-size: 16px;
  font-weight: bold;
  margin: 0 0 8px;
  font-family: 'MiSans-Bold', Arial, sans-serif;
}

.modal-result-title mark {
  background: #ffeb3b;
  color: #000;
  padding: 2px 4px;
  border-radius: 3px;
}

.modal-result-path {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  font-size: 12px;
  opacity: 0.7;
}

.modal-result-actions {
  margin-left: 16px;
}

.modal-goto-btn {
  padding: 8px 16px;
  background: rgba(0, 106, 255, 0.2);
  border: 1px solid rgba(0, 106, 255, 0.3);
  color: #87ceeb;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s ease;
}

.modal-goto-btn:hover {
  background: rgba(0, 106, 255, 0.3);
  border-color: rgba(0, 106, 255, 0.5);
  color: white;
}

/* 弹窗分页 */
.modal-pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
  padding: 24px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.modal-page-btn {
  padding: 8px 16px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(255, 255, 255, 0.05);
  color: white;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.modal-page-btn:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.3);
}

.modal-page-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.modal-page-info {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.8);
}

/* 人工支持区域 */
.support-section {
  margin-top: 60px;
  padding: 40px 20px;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 16px;
  text-align: center;
}

.support-title {
  font-size: 24px;
  font-family: 'MiSans-Bold', Arial, sans-serif;
  margin-bottom: 30px;
  color: rgba(255, 255, 255, 0.9);
}

.support-options {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
  max-width: 800px;
  margin: 0 auto;
}

.support-option {
  text-align: center;
}

.support-email,
.support-call,
.support-chat {
  display: inline-block;
  position: relative;
  margin-bottom: 15px;
  cursor: pointer;
  font-size: 20px;
  font-family: 'MiSans-Bold', Arial, sans-serif;
  padding: 18px 28px;
  border-radius: 12px;
  transition: all 0.3s ease;
  background: rgba(255, 255, 255, 0.05);
  border: 2px solid rgba(255, 255, 255, 0.1);
  min-width: 260px;
}

.support-email:hover,
.support-chat:hover:not(.disabled) {
  transform: scale(1.05);
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.2);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
}

.support-chat.disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.support-description {
  font-size: 14px;
  opacity: 0.8;
  margin: 0;
  color: rgba(255, 255, 255, 0.7);
}

.chat-status {
  margin-top: 10px;
  font-size: 14px;
}

.status-online,
.status-offline {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
}

.status-dot.online {
  background: #4caf50;
  animation: pulse 2s infinite;
}

.status-dot.offline {
  background: #ff9800;
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(76, 175, 80, 0.7);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(76, 175, 80, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(76, 175, 80, 0);
  }
}

.status-online {
  color: #4caf50;
}

.status-offline {
  color: #ff9800;
}

/* 加载状态 */
.loading {
  text-align: center;
  padding: 60px 20px;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid rgba(255, 255, 255, 0.1);
  border-left-color: #006aff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* 错误状态 */
.error {
  text-align: center;
  padding: 60px 20px;
}

.error-icon {
  font-size: 48px;
  margin-bottom: 20px;
}

.retry-btn {
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  background: #006aff;
  color: white;
  cursor: pointer;
  margin-top: 20px;
  transition: background 0.3s ease;
}

.retry-btn:hover {
  background: #0052cc;
}

/* 无结果 */
.no-results {
  text-align: center;
  padding: 60px 20px;
}

.no-results-icon {
  font-size: 64px;
  margin-bottom: 20px;
}

.no-results h3 {
  font-size: 24px;
  margin: 0 0 10px;
  font-family: 'MiSans-Bold', Arial, sans-serif;
}

.no-results p {
  opacity: 0.8;
  margin-bottom: 30px;
}

/* 响应式 */
@media (max-width: 768px) {
  .search-result {
    padding: 15px;
  }
  
  .search-title {
    font-size: 28px;
  }
  
  .research-box {
    margin: 0 15px;
  }
  
  .main-result-card {
    padding: 24px;
  }
  
  .main-result-title {
    font-size: 22px;
  }
  
  .modal-content {
    width: 95%;
    margin: 10px;
  }
  
  .modal-header {
    padding: 20px;
  }
  
  .modal-results-list {
    padding: 20px;
  }
  
  .modal-result-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
  
  .modal-result-actions {
    margin-left: 0;
    width: 100%;
  }
  
  .modal-goto-btn {
    width: 100%;
    text-align: center;
  }
  
  .support-options {
    grid-template-columns: 1fr;
    gap: 20px;
  }
}

@media (max-width: 480px) {
  .search-title {
    font-size: 24px;
  }
  
  .main-result-title {
    font-size: 20px;
  }
  
  .main-result-card {
    padding: 20px;
  }
  
  .modal-pagination {
    flex-direction: column;
    gap: 15px;
  }
}
</style>